/**
 * Four-point perspective correction — pure maths, no DOM.
 *
 * A photo of a whiteboard is never square: you stand off to one side, so a
 * grid of stickies arrives as a trapezoid and every column reads as a
 * diagonal. Correcting that needs a HOMOGRAPHY, which is a projective map;
 * an affine transform (what `sharp` and CSS 2D transforms offer) cannot
 * represent it, and rotating instead will convince you the problem is fixed
 * when it is not.
 *
 * The corners come from the USER dragging them, never from edge detection.
 * Auto-detecting a whiteboard's outline is unreliable — white board, white
 * wall, no frame, someone standing in front of it — and its failure mode is
 * the worst kind: a silently sheared board where nothing looks broken.
 *
 * Kept pure and DOM-free so the arithmetic can be checked directly, the way
 * `geometry.ts` is.
 */
/** An 8x8 solve by Gaussian elimination with partial pivoting. */
function solve8(a, b) {
    const n = 8;
    const m = a.map((row, i) => [...row, b[i]]);
    for (let col = 0; col < n; col++) {
        // Pivot on the largest remaining magnitude, or the system is unstable.
        let piv = col;
        for (let r = col + 1; r < n; r++)
            if (Math.abs(m[r][col]) > Math.abs(m[piv][col]))
                piv = r;
        if (Math.abs(m[piv][col]) < 1e-12)
            return null; // degenerate quad
        if (piv !== col) {
            const t = m[piv];
            m[piv] = m[col];
            m[col] = t;
        }
        const d = m[col][col];
        for (let c = col; c <= n; c++)
            m[col][c] /= d;
        for (let r = 0; r < n; r++) {
            if (r === col)
                continue;
            const f = m[r][col];
            if (!f)
                continue;
            for (let c = col; c <= n; c++)
                m[r][c] -= f * m[col][c];
        }
    }
    return m.map((row) => row[n]);
}
/**
 * The 3x3 homography (row-major, h8 fixed at 1) mapping four `from` points
 * onto four `to` points. Returns null when the quad is degenerate — three
 * points in a line, or a corner dragged onto another.
 */
export function homography(from, to) {
    if (from.length !== 4 || to.length !== 4)
        return null;
    const a = [];
    const b = [];
    for (let i = 0; i < 4; i++) {
        const { x, y } = from[i];
        const { x: u, y: v } = to[i];
        a.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
        b.push(u);
        a.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
        b.push(v);
    }
    const h = solve8(a, b);
    return h ? [...h, 1] : null;
}
/** Push one point through a homography. */
export function applyH(h, p) {
    const d = h[6] * p.x + h[7] * p.y + h[8];
    if (!d)
        return { x: 0, y: 0 };
    return {
        x: (h[0] * p.x + h[1] * p.y + h[2]) / d,
        y: (h[3] * p.x + h[4] * p.y + h[5]) / d,
    };
}
/**
 * A sensible output size for a quad: the longest opposing edges, so nothing
 * in the photo is squeezed, capped so a 12MP phone photo does not become a
 * 50MP canvas.
 */
export function rectSizeFor(quad, maxEdge = 2200) {
    const d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    const w = Math.max(d(quad[0], quad[1]), d(quad[3], quad[2]));
    const h = Math.max(d(quad[0], quad[3]), d(quad[1], quad[2]));
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    return { w: Math.max(1, Math.round(w * scale)), h: Math.max(1, Math.round(h * scale)) };
}
/**
 * Warp the quad (clockwise from top-left) out of `src` into a straight
 * `outW × outH` rectangle.
 *
 * Inverse mapping with bilinear sampling: we walk the OUTPUT pixels and ask
 * where each came from, which is the only way to get an output with no
 * holes. Sampling forward from the source leaves gaps wherever the
 * transform stretches.
 */
export function warpToRect(src, quad, outW, outH) {
    // dst → src, because we iterate the destination.
    const h = homography([{ x: 0, y: 0 }, { x: outW, y: 0 }, { x: outW, y: outH }, { x: 0, y: outH }], quad);
    if (!h)
        return null;
    const out = new Uint8ClampedArray(outW * outH * 4);
    const { data, width: sw, height: sh } = src;
    for (let y = 0; y < outH; y++) {
        for (let x = 0; x < outW; x++) {
            const p = applyH(h, { x: x + 0.5, y: y + 0.5 });
            const o = (y * outW + x) * 4;
            // Outside the photo is transparent, never a smeared edge pixel.
            if (p.x < 0 || p.y < 0 || p.x >= sw || p.y >= sh) {
                out[o + 3] = 0;
                continue;
            }
            const x0 = Math.floor(p.x);
            const y0 = Math.floor(p.y);
            const x1 = Math.min(x0 + 1, sw - 1);
            const y1 = Math.min(y0 + 1, sh - 1);
            const fx = p.x - x0;
            const fy = p.y - y0;
            const i00 = (y0 * sw + x0) * 4;
            const i10 = (y0 * sw + x1) * 4;
            const i01 = (y1 * sw + x0) * 4;
            const i11 = (y1 * sw + x1) * 4;
            const w00 = (1 - fx) * (1 - fy);
            const w10 = fx * (1 - fy);
            const w01 = (1 - fx) * fy;
            const w11 = fx * fy;
            for (let c = 0; c < 4; c++) {
                out[o + c] = data[i00 + c] * w00 + data[i10 + c] * w10 + data[i01 + c] * w01 + data[i11 + c] * w11;
            }
        }
    }
    return { data: out, width: outW, height: outH };
}
/** The default quad: the whole photo, inset a little so the handles are grabbable. */
export function defaultQuad(w, h, inset = 0.04) {
    const ix = w * inset;
    const iy = h * inset;
    return [
        { x: ix, y: iy },
        { x: w - ix, y: iy },
        { x: w - ix, y: h - iy },
        { x: ix, y: h - iy },
    ];
}
/**
 * Is this quad usable? Rejects the shapes that produce a garbage warp:
 * anything too small, and any corner that has been dragged past its
 * neighbours so the outline crosses itself.
 */
export function quadIsSane(quad, minEdge = 24) {
    if (quad.length !== 4)
        return false;
    if (!quad.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y)))
        return false;
    for (let i = 0; i < 4; i++) {
        const a = quad[i];
        const b = quad[(i + 1) % 4];
        if (Math.hypot(a.x - b.x, a.y - b.y) < minEdge)
            return false;
    }
    // A simple (non-self-intersecting) quad has a consistent turn direction.
    let sign = 0;
    for (let i = 0; i < 4; i++) {
        const a = quad[i];
        const b = quad[(i + 1) % 4];
        const c = quad[(i + 2) % 4];
        const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
        if (!cross)
            continue;
        const s = Math.sign(cross);
        if (!sign)
            sign = s;
        else if (s !== sign)
            return false;
    }
    return sign !== 0;
}
